/**
 * @file useScrollAffordance.js
 * @version 1.2.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-25
 * @modified 2026-10-12
 * @description Does this panel have more below, and does it have more above.
 *
 *              A scrolling panel whose content happens to end level with its
 *              frame is indistinguishable from one that has finished. Both side
 *              panels in the calculator do exactly that, so fields at the foot
 *              of the details panel and figures at the foot of the summary went
 *              unnoticed by anyone who did not think to scroll.
 *
 *              Returns the ref to attach plus, for each axis, whether there is
 *              anything either side and how much is still to come — `remaining`
 *              vertically and `remainingX` horizontally, 1 at the start and 0 at
 *              the end. The cue uses it as its opacity, so it thins out as you
 *              travel rather than switching off at a threshold.
 */

import { useState, useEffect, useRef } from 'react';

export default function useScrollAffordance(deps = []) {
  const ref = useRef(null);
  const [state, setState] = useState({
    above: false, below: false, remaining: 0,
    left: false, right: false, remainingX: 0
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const measure = () => {
      // `remaining` rather than a boolean: the cue fades across the scroll
      // instead of vanishing at the end, so it reads as "there is this much
      // further to go" rather than blinking off at an arbitrary threshold.
      const maxY = el.scrollHeight - el.clientHeight;
      const maxX = el.scrollWidth - el.clientWidth;
      const progressY = maxY <= 0 ? 1 : Math.min(1, Math.max(0, el.scrollTop / maxY));
      const progressX = maxX <= 0 ? 1 : Math.min(1, Math.max(0, el.scrollLeft / maxX));
      setState({
        above: el.scrollTop > 2,
        below: maxY > 2 && progressY < 1,
        remaining: maxY > 2 ? 1 - progressY : 0,
        // Horizontal, for wide tables. Measured here rather than in a second
        // hook so a box that scrolls both ways is described by one observer.
        left: el.scrollLeft > 2,
        right: maxX > 2 && progressX < 1,
        remainingX: maxX > 2 ? 1 - progressX : 0
      });
    };

    measure();
    el.addEventListener('scroll', measure, { passive: true });
    // Content grows and shrinks as sections open and figures appear, and the
    // panel itself resizes with the window — both change what is reachable, and
    // neither fires a scroll event.
    // Not every environment has ResizeObserver (older browsers, the test
    // runner's jsdom): without it the hint still follows scrolling.
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (observer) {
      observer.observe(el);
      for (const child of el.children) observer.observe(child);
    }

    return () => {
      el.removeEventListener('scroll', measure);
      if (observer) observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return [ref, state];
}
